/**
 * PayrollDB — the "Payroll" master database on the Search page. Reads the
 * current payroll database: the app's own paystubs PLUS imported Knit pay
 * stubs. Top: grand totals (gross, CPP, EI, taxes, deductions, net,
 * vacation). One row per employee with their totals; expanding an employee
 * shows every pay period with full detail. Batch "Import Paystubs" drops a
 * stack of Knit PDFs and auto-matches each to an employee by the name
 * printed inside. Per-period and bulk delete with confirmation popups.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import ImportedStubCard from "../payroll/ImportedStubCard";
import { useStubDrop, StubDropOverlay, importStubsInBatches } from "../payroll/stubDrop";
import { StubImportProgress } from "../payroll/StubImportProgress";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtH = (n) => `${Number(n || 0).toLocaleString("en-CA", { maximumFractionDigits: 2 })}h`;

const TH = "px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest text-white/50";
const TDR = "px-2.5 py-1.5 text-right text-xs text-white/85";

const TotalsCells = ({ t, strong }) => (
  <>
    <td className={`${TDR} ${strong ? "font-semibold text-white" : ""}`}>{fmtH(t.hours)}</td>
    <td className={`${TDR} ${strong ? "font-semibold text-white" : ""}`}>{fmtMoney(t.gross)}</td>
    <td className={TDR}>{fmtMoney(t.cpp)}</td>
    <td className={TDR}>{fmtMoney(t.ei)}</td>
    <td className={TDR}>{fmtMoney(t.federal_tax + t.provincial_tax)}</td>
    <td className={TDR}>{fmtMoney(t.deductions)}</td>
    <td className={`${TDR} font-semibold text-[#fb923c]`}>{fmtMoney(t.net)}</td>
    <td className={TDR}>{fmtMoney(t.vacation)}</td>
  </>
);

export default function PayrollDB() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(0); // 0 = all years
  const [expanded, setExpanded] = useState(() => new Set());
  const [uploading, setUploading] = useState(false);
  const [selPeriods, setSelPeriods] = useState(() => new Map()); // id -> kind
  const [confirmDel, setConfirmDel] = useState(null); // {kind:'one'|'bulk', period?}
  const [deleting, setDeleting] = useState(false);
  const [viewImport, setViewImport] = useState(null); // import_id → readable stub card
  const [progress, setProgress] = useState(null);     // {done,total,pct} while importing
  const fileRef = useRef(null);
  const { dragging, dropProps } = useStubDrop((files) => onImport(files), { disabled: uploading });

  const load = async (silent = true) => {
    try {
      const { data } = await api.get("/master/payroll/summary", { params: { year: year || 0 } });
      setData(data);
    } catch {
      if (!silent) toast.error("Could not load the payroll database");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(false); }, [year]);

  const onImport = async (files) => {
    const list = [...(files || [])];
    if (!list.length) { toast.error("No PDF or CSV pay stubs in that drop"); return; }
    setUploading(true);
    setProgress({ done: 0, total: list.length, pct: 0, current: [] });
    try {
      const data = await importStubsInBatches(api, list, setProgress);
      const rs = data.results || [];
      const ok = rs.filter((r) => r.status === "imported").length;
      const dup = rs.filter((r) => r.status === "duplicate").length;
      const blank = rs.filter((r) => r.status === "imported" && r.blank).length;
      const bad = rs.filter((r) => r.status === "unmatched" || r.status === "error");
      if (ok) toast.success(`Imported ${ok} pay stub${ok === 1 ? "" : "s"}`);
      if (dup) toast.info(`${dup} already on file — skipped, not added again`);
      if (blank) toast.warning(`${blank} blank $0 stub${blank === 1 ? "" : "s"} flagged`);
      bad.forEach((r) => toast.error(`${r.filename}: ${r.stub_name ? `stub says "${r.stub_name}" — ` : ""}${r.detail || "not imported"}`));
      if (!rs.length) toast.info("No pay stubs found in those files");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 1200);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (eid) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(eid) ? n.delete(eid) : n.add(eid);
      return n;
    });

  const allPeriods = (data?.employees || []).flatMap((e) => e.periods);
  const togglePeriod = (p) =>
    setSelPeriods((m) => {
      const n = new Map(m);
      n.has(p.id) ? n.delete(p.id) : n.set(p.id, p.kind);
      return n;
    });

  const deleteOne = async () => {
    const p = confirmDel.period;
    setDeleting(true);
    try {
      if (p.kind === "knit") await api.delete(`/payroll/imported-paystubs/${p.id}`);
      else await api.delete(`/payroll/paystub/${p.id}`);
      toast.success("Pay period deleted");
      setConfirmDel(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete it");
    } finally {
      setDeleting(false);
    }
  };

  const deleteBulk = async () => {
    setDeleting(true);
    const paystub_ids = [], import_ids = [];
    selPeriods.forEach((kind, id) => (kind === "knit" ? import_ids : paystub_ids).push(id));
    try {
      const { data } = await api.post("/master/payroll/bulk-delete", { paystub_ids, import_ids });
      toast.success(`Deleted ${data.deleted_paystubs + data.deleted_imports} pay period${data.deleted_paystubs + data.deleted_imports === 1 ? "" : "s"}`);
      setSelPeriods(new Map());
      setConfirmDel(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const g = data?.grand;

  return (
    <div className="relative space-y-6" data-testid="master-db-payroll" {...dropProps}>
      <StubDropOverlay show={dragging} testId="master-payroll-drop-overlay" />
      <StubImportProgress progress={progress} testId="master-import-progress" />
      {viewImport && <ImportedStubCard importId={viewImport} onClose={() => setViewImport(null)} />}
      {/* header + import */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          Payroll · master database · app paystubs + imported stubs · drop PDFs, CSVs or folders here to import
        </p>
        <div className="flex items-center gap-2">
          {data?.years?.length > 0 && (
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              data-testid="payroll-year-filter"
              className="rounded-md border border-white/15 bg-white/[0.05] px-2.5 py-2 font-mono text-xs text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none">
              <option value={0}>All years</option>
              {data.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          <input ref={fileRef} type="file" accept=".pdf,application/pdf,.csv,text/csv" multiple className="hidden"
            onChange={(e) => onImport(e.target.files)} data-testid="payroll-import-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="payroll-import-btn"
            className="flex items-center gap-2 rounded-md bg-[#1a3fd1] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1633a3] disabled:opacity-50">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
            {uploading ? "Importing…" : "Import paystubs"}
          </button>
        </div>
      </div>

      {loading && <p className="text-xs text-white/40" data-testid="payroll-loading">Loading…</p>}

      {!loading && data && data.employees.length === 0 && (
        <p className="rounded-lg border border-white/10 px-3 py-8 text-center text-xs text-white/35" data-testid="payroll-empty">
          No pay periods{year ? ` in ${year}` : ""} yet — finalize paystubs on the Payroll page or import pay-stub PDFs / CSV exports above.
        </p>
      )}

      {!loading && data && data.employees.length > 0 && (
        <>
          {/* grand totals strip */}
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-8" data-testid="payroll-grand-totals">
            {[
              ["Employees", data.employee_count],
              ["Hours", fmtH(g.hours)],
              ["Gross", fmtMoney(g.gross)],
              ["CPP", fmtMoney(g.cpp)],
              ["EI", fmtMoney(g.ei)],
              ["Taxes", fmtMoney(g.federal_tax + g.provincial_tax)],
              ["Net", fmtMoney(g.net)],
              ["Vacation", fmtMoney(g.vacation)],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">{k}</p>
                <p className="mt-0.5 text-sm font-bold text-white" data-testid={`payroll-grand-${k.toLowerCase()}`}>{v}</p>
              </div>
            ))}
          </div>

          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="font-mono text-xs font-semibold uppercase tracking-widest text-white/70" data-testid="payroll-heading">
                Employees
                <span className="ml-2 text-white/35">{data.employee_count}</span>
              </h2>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-white/55">
                  <input type="checkbox" className="h-3.5 w-3.5 accent-[#1a3fd1]"
                    checked={selPeriods.size === allPeriods.length && allPeriods.length > 0}
                    onChange={() => setSelPeriods(selPeriods.size === allPeriods.length ? new Map() : new Map(allPeriods.map((p) => [p.id, p.kind])))}
                    data-testid="payroll-select-all" />
                  All periods
                </label>
                {selPeriods.size > 0 && (
                  <button type="button" onClick={() => setConfirmDel({ kind: "bulk" })}
                    data-testid="payroll-delete-selected"
                    className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-red-700">
                    <Trash2 className="h-3.5 w-3.5" /> Delete ({selPeriods.size})
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full min-w-[980px] border-collapse" data-testid="payroll-table">
                <thead>
                  <tr className="bg-white/5">
                    <th className={TH}></th>
                    <th className={TH}>Employee / pay period</th>
                    <th className={`${TH} text-right`}>Hours</th>
                    <th className={`${TH} text-right`}>Gross</th>
                    <th className={`${TH} text-right`}>CPP</th>
                    <th className={`${TH} text-right`}>EI</th>
                    <th className={`${TH} text-right`}>Taxes</th>
                    <th className={`${TH} text-right`}>Deduct.</th>
                    <th className={`${TH} text-right`}>Net</th>
                    <th className={`${TH} text-right`}>Vacation</th>
                    <th className={TH}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.employees.map((e) => [
                    <tr key={e.employee_id} className="border-t border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                      data-testid={`payroll-emp-${e.employee_id}`}>
                      <td className="px-2.5 py-1.5">
                        <button type="button" onClick={() => toggle(e.employee_id)}
                          data-testid={`payroll-expand-${e.employee_id}`}
                          aria-label={expanded.has(e.employee_id) ? "Collapse employee" : "Expand employee"}
                          className="rounded border border-white/15 p-1 text-white/60 hover:border-white/30 hover:text-white">
                          {expanded.has(e.employee_id) ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                        </button>
                      </td>
                      <td className="px-2.5 py-1.5 text-xs font-semibold text-white">
                        {e.name}
                        <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-white/35">
                          {e.totals.periods} period{e.totals.periods === 1 ? "" : "s"}
                        </span>
                      </td>
                      <TotalsCells t={e.totals} strong />
                      <td></td>
                    </tr>,
                    expanded.has(e.employee_id) && e.periods.map((p) => (
                      <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]"
                        data-testid={`payroll-period-${p.id}`}>
                        <td className="px-2.5 py-1 pl-8">
                          <input type="checkbox" className="h-3.5 w-3.5 accent-[#1a3fd1]"
                            checked={selPeriods.has(p.id)} onChange={() => togglePeriod(p)}
                            data-testid={`payroll-sel-${p.id}`} aria-label="Select period" />
                        </td>
                        <td className="px-2.5 py-1 text-[11px] text-white/70">
                          {p.kind === "knit" ? (
                            <button type="button" onClick={() => setViewImport(p.id)}
                              data-testid={`payroll-open-${p.id}`}
                              title="Open as a readable pay-stub card (download the original from there)"
                              className="text-left underline-offset-2 hover:text-white hover:underline">
                              {p.label}
                            </button>
                          ) : p.label}
                          <span className={`ml-2 rounded-full px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider ${p.kind === "knit" ? "bg-purple-500/15 text-purple-300" : "bg-[#1a3fd1]/20 text-[#8fa8ff]"}`}>
                            {p.kind === "knit" ? (p.source === "knit_csv" ? "Imported CSV" : "Imported") : "App"}
                          </span>
                          {p.blank && (
                            <span className="ml-1 rounded-full bg-[#5a4208] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-[#fde68a]"
                              title="This imported stub has $0 on it — recorded so the period isn't treated as missing"
                              data-testid={`payroll-blank-${p.id}`}>Blank $0</span>
                          )}
                          {p.overtime_hours > 0 && <span className="ml-1 text-[9px] text-amber-300/70">+{p.overtime_hours}h OT</span>}
                          <span className="block text-[9px] text-white/30">{p.location}{p.rate ? ` · ${fmtMoney(p.rate)}/h` : ""}</span>
                        </td>
                        <TotalsCells t={p} />
                        <td className="px-2.5 py-1 text-right">
                          <button type="button" onClick={() => setConfirmDel({ kind: "one", period: p, empName: e.name })}
                            data-testid={`payroll-del-${p.id}`} aria-label="Delete period"
                            className="rounded p-1 text-white/25 hover:bg-red-500/15 hover:text-red-400">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* delete confirmation popup */}
      {confirmDel && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4"
          onClick={() => !deleting && setConfirmDel(null)}>
          <div className="w-full max-w-sm rounded-xl border border-white/15 bg-[#0d0d0d] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()} data-testid="payroll-del-confirm-dialog">
            <h3 className="text-sm font-bold text-white">
              {confirmDel.kind === "bulk"
                ? `Delete ${selPeriods.size} pay period${selPeriods.size === 1 ? "" : "s"}?`
                : `Delete ${confirmDel.period.label} for ${confirmDel.empName}?`}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-white/50">
              This permanently removes {confirmDel.kind === "bulk" ? "them" : "it"} from the payroll database
              {confirmDel.kind === "one" && confirmDel.period.kind === "knit" ? " (the filed PDF/CSV goes too)" : ""}.
              Year-to-date figures recompute without {confirmDel.kind === "bulk" ? "them" : "it"}. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDel(null)} disabled={deleting}
                data-testid="payroll-del-cancel"
                className="rounded-md border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:text-white">
                Cancel
              </button>
              <button type="button" onClick={confirmDel.kind === "bulk" ? deleteBulk : deleteOne} disabled={deleting}
                data-testid="payroll-del-confirm"
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
